/**
 * FeatureFlagsContext
 *
 * Fetches the backend's `GET /api/config/features` once at app startup and
 * makes the flag map available to every component via `useFeatureFlags()`.
 *
 * Backend `.env`:
 *   DEAL_SCANNER_ENABLED=false   # hidden for public V1 launch
 *   DEAL_SCANNER_ENABLED=true    # unlocked for internal / staging
 *
 * While the flags load, we treat them as "off" so we NEVER flash a gated
 * entry point on first paint.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_FLAGS = Object.freeze({
  deal_scanner_enabled: false,
});

const FeatureFlagsContext = createContext({
  flags: DEFAULT_FLAGS,
  loading: true,
});

export function FeatureFlagsProvider({ children }) {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/config/features`, { withCredentials: false })
      .then((res) => {
        if (!cancelled) setFlags({ ...DEFAULT_FLAGS, ...(res.data || {}) });
      })
      .catch(() => {
        // Network error → keep safe defaults (feature stays hidden).
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ flags, loading }), [flags, loading]);
  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

/** Convenience selector for the most-used flag. */
export function useIsDealScannerEnabled() {
  return useContext(FeatureFlagsContext).flags.deal_scanner_enabled;
}
